import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { Modal } from 'antd';
import AppContext from '../contexts/AppContext';

const statusStyles = {
  active: 'bg-green-100 text-green-700 ring-1 ring-green-200',
  paid: 'bg-green-100 text-green-700 ring-1 ring-green-200',
  due: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200',
  unpaid: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  overdue: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  past_due: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  'past-due': 'bg-red-100 text-red-700 ring-1 ring-red-200',
  paused: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200',
  canceled: 'bg-gray-200 text-gray-700 ring-1 ring-gray-300',
  cancel: 'bg-gray-200 text-gray-700 ring-1 ring-gray-300',
  cancelled: 'bg-gray-200 text-gray-700 ring-1 ring-gray-300',
  failed: 'bg-red-100 text-red-700 ring-1 ring-red-200',
};

const statusLabel = (value = '') => value.charAt(0).toUpperCase() + value.slice(1);

const formatCurrency = (amount, currency = 'AUD') =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency.toUpperCase() }).format(amount || 0);

const RESUBSCRIBE_ENDPOINT_PLACEHOLDER = 'TBD';
const CANCEL_STATUSES = new Set(['cancel', 'canceled', 'cancelled', 'expired']);
const OVERDUE_STATUSES = new Set(['overdue', 'past_due', 'past-due']);

const USER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: USER_TIME_ZONE,
    })
    : '--';

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: USER_TIME_ZONE,
    })
    : '--';

// const formatExpiry = (value) => {
//   if (!value) return '--';
//   const d = new Date(value);
//   if (d.getFullYear() === 2099 && d.getMonth() === 11 && d.getDate() === 31) return 'Continuously Active';
//   return formatDate(value);
// };

const formatNote = (note) => {
  if (!note) return '';
  // Try to handle structured pause history
  try {
    const data = JSON.parse(note);
    if (data?.pauseHistory?.length) {
      const latest = data.pauseHistory[data.pauseHistory.length - 1];
      const base = `Paused ${formatDateTime(latest.startAt)} -> ${formatDateTime(latest.endAt)}`;
      if (Array.isArray(latest.extensions) && latest.extensions.length) {
        const ext = latest.extensions
          .map((e) => `${formatDateTime(e.startAt)} -> ${formatDateTime(e.endAt)}`)
          .join('; ');
        return `${base} (extended: ${ext})`;
      }
      return base;
    }
  } catch (e) {
    // Not JSON, fall back
  }

  const parsedDate = Date.parse(note);
  if (!Number.isNaN(parsedDate)) {
    return formatDateTime(note);
  }
  return note;
};

const parsePauseNote = (note) => {
  if (!note || typeof note !== 'string') return null;
  const lower = note.toLowerCase();
  const hasPauseKeyword = lower.includes('pausehistory') || lower.includes('pausehistroy');

  const tryParse = (str) => {
    try {
      const data = JSON.parse(str);
      if (data?.pauseHistory?.length) return data.pauseHistory;
    } catch (e) {
      return null;
    }
    return null;
  };

  // 1) direct parse
  const direct = tryParse(note);
  if (direct) return direct;

  // 1.5) unescape common escaped quotes (e.g. "{\"pauseHistory\":...}")
  const unescaped = note.replace(/\\"/g, '"');
  if (unescaped !== note) {
    const parsed = tryParse(unescaped);
    if (parsed) return parsed;
  }

  if (!hasPauseKeyword) return null;

  // 2) if string contains pauseHistory blob inside other text, extract JSON substring
  const match = note.match(/\{.*"pauseHist(?:ory|roy)".*\}/si);
  if (match?.[0]) {
    const extracted = tryParse(match[0]);
    if (extracted) return extracted;
  }

  // 3) handle cases like pauseHistory:[...] without outer braces
  const arrMatch = note.match(/pauseHist(?:ory|roy)"?\s*:\s*(\[[\s\S]*\])/i);
  if (arrMatch?.[1]) {
    const wrapped = `{"pauseHistory":${arrMatch[1]}}`;
    const parsed = tryParse(wrapped);
    if (parsed) return parsed;
  }

  return null;
};

const parseGenericJsonNote = (note) => {
  if (!note || typeof note !== 'string') return null;

  const tryParse = (str) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  };

  const direct = tryParse(note);
  if (direct && typeof direct === 'object') return direct;

  const unescaped = note.replace(/\\"/g, '"');
  if (unescaped !== note) {
    const parsed = tryParse(unescaped);
    if (parsed && typeof parsed === 'object') return parsed;
  }

  return null;
};

const Billing = () => {
  const { token, selectedRole, userInfo, subscriptionGate } = useContext(AppContext);
  const location = useLocation();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteDetail, setNoteDetail] = useState(null);
  const [noteModalType, setNoteModalType] = useState('pause'); // 'pause' | 'json'
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const fetchBilling = async () => {
    if (!token || !selectedRole?.id) return;
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BILLING_API_KEY}/api/private/billing/check-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ venueId: selectedRole.id }),
        },
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || resp.statusText || 'Failed to load billing');
      }
      const data = await resp.json();
      setBilling(data?.data || data);
    } catch (err) {
      setError(err.message || 'Unable to load billing right now');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole?.id, token]);

  const subscription = billing?.subscription;
  const payments = billing?.payments || [];
  const scopeList = userInfo?.roleInfo?.[selectedRole?.type] || [];
  const currentScope = scopeList.find((r) => r.id === selectedRole?.id);
  const hasStripeCustomerId = Boolean(currentScope?.stripeCustomerId || currentScope?.stripeCutsomerId);
  const scopeName =
    currentScope?.name ||
    currentScope?.venueName ||
    currentScope?.title ||
    currentScope?.displayName ||
    subscription?.scopeId ||
    '--';

  const noData = !loading && !error && billing && subscription === null;
  const showSubscriptionLockBanner = Boolean(location.state?.subscriptionGateBlocked) || Boolean(subscriptionGate?.isBlocked && subscriptionGate?.billingOnly);

  const subscriptionNotePause = parsePauseNote(subscription?.notes);
  const subscriptionNoteJson = !subscriptionNotePause ? parseGenericJsonNote(subscription?.notes) : null;

  const statusClass = statusStyles[subscription?.status] || 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
  const normalizedStatus = (subscription?.status || '').toLowerCase();
  const isCanceled = CANCEL_STATUSES.has(normalizedStatus);

  const resubscribeEndpoint =
    subscription?.resubscribeEndpoint ||
    subscription?.resubscribePath ||
    subscription?.resubscribeUrl ||
    RESUBSCRIBE_ENDPOINT_PLACEHOLDER;
  const canResubscribe = resubscribeEndpoint !== RESUBSCRIBE_ENDPOINT_PLACEHOLDER;

  const portalEndpoint = `${process.env.REACT_APP_BILLING_API_KEY}/api/private/billing/portal-session`;

  const handleResubscribe = () => {
    if (!canResubscribe) {
      Modal.info({
        title: 'Resubscribe endpoint pending',
        content: `API path: ${RESUBSCRIBE_ENDPOINT_PLACEHOLDER}`,
      });
      return;
    }
    window.open(resubscribeEndpoint, '_blank', 'noopener,noreferrer');
  };

  const handleOpenPortal = async () => {
    if (!token || !selectedRole?.id) {
      Modal.info({
        title: 'Missing venue',
        content: 'Please select a venue before opening the billing portal.',
      });
      return;
    }

    setPortalLoading(true);
    try {
      const returnUrl = window.location.href;
      const resp = await fetch(portalEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ venueId: selectedRole.id, returnUrl }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || resp.statusText || 'Failed to open billing portal');
      }
      const data = await resp.json().catch(() => ({}));
      const url =
        data?.url ||
        data?.portalUrl ||
        data?.sessionUrl ||
        data?.data?.url ||
        data?.data?.portalUrl ||
        data?.data?.sessionUrl;
      if (!url) {
        throw new Error('Billing portal link not found in response');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      Modal.error({
        title: 'Unable to open billing portal',
        content: err.message || 'Please try again later.',
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleStartCancellation = async () => {
    setCancelModalOpen(false);
    await handleOpenPortal();
  };

  return (
    <div className="w-[90%] max-w-7xl mx-auto pt-2 pb-6 space-y-5">
      <div className="-mt-1 flex items-center justify-end">
        <button
          type="button"
          onClick={fetchBilling}
          className="group inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-[0_12px_24px_rgba(37,99,235,0.24)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-[0_18px_30px_rgba(37,99,235,0.28)] active:translate-y-0"
        >
          <ArrowPathIcon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180" />
          Refresh
        </button>
      </div>

      {showSubscriptionLockBanner && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5" />
          <span>Your subscription has ended. Billing remains available so you can update payment details or resubscribe.</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-100">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-600">
          <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" />
          Loading billing details...
        </div>
      ) : noData ? (
        <div className="rounded-lg bg-gradient-to-r from-blue-50 via-white to-blue-50 px-5 py-6 text-gray-800 ring-1 ring-blue-100 shadow-sm">
          <div className="text-lg font-semibold text-blue-900">No automated subscription</div>
          <p className="mt-2 text-sm text-gray-700">
            You're not currently using our automated subscription service for this venue, so billing details aren't available here.
            If you'd like to set it up or need help, please email{' '}
            <a className="underline font-medium text-blue-700" href="mailto:info@platypus360.com">
              info@platypus360.com
            </a>.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="group rounded-[22px] border border-[#e7ecf8] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.045)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(30,52,120,0.08)]">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Status</div>
                <CheckCircleIcon className="h-5 w-5 text-green-500 transition-transform duration-200 group-hover:scale-110" />
              </div>
              <div className="mt-3">
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                  {statusLabel(subscription?.status || 'unknown')}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-500">Subscription is currently {subscription?.status || 'unknown'}.</p>
            </div>

            <div className="group rounded-[22px] border border-[#e7ecf8] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.045)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(30,52,120,0.08)]">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Next payment</div>
                <CalendarDaysIcon className="h-5 w-5 text-blue-500 transition-transform duration-200 group-hover:scale-110" />
              </div>
              <div className="mt-3 text-xl font-semibold text-gray-900">
                {formatDate(subscription?.nextDueDate)}
              </div>
              <p className="mt-2 text-xs text-gray-500">Last invoice: {formatDate(subscription?.lastInvoicedDate)}</p>
            </div>

            <div className="group rounded-[22px] border border-[#e7ecf8] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.045)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(30,52,120,0.08)]">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Plan</div>
                <ReceiptPercentIcon className="h-5 w-5 text-indigo-500 transition-transform duration-200 group-hover:scale-110" />
              </div>
              <div className="mt-3 text-xl font-semibold text-gray-900">
                {formatCurrency(subscription?.amount, subscription?.currency)}
              </div>
              <p className="mt-2 text-xs text-gray-500 capitalize">
                {subscription?.period || 'Period not set'}
              </p>
            </div>

            <div className="group rounded-[22px] border border-[#e7ecf8] bg-white p-4 shadow-[0_12px_28px_rgba(15,23,42,0.045)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(30,52,120,0.08)]">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">Scope</div>
                <div className="text-xs font-medium text-blue-600 uppercase">{subscription?.scopeType}</div>
              </div>
              <div className="mt-3 text-lg font-semibold text-gray-900 truncate">{scopeName}</div>
              <p className="mt-2 text-xs text-gray-500">ID: {subscription?.scopeId || '--'}</p>
              {/* <p className="mt-2 text-xs text-gray-500">Subscription ID: {subscription?.id || '--'}</p> */}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-[24px] border border-[#e7ecf8] bg-white p-5 shadow-[0_14px_30px_rgba(15,23,42,0.05)] lg:col-span-2">
              <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
                  <p className="text-sm text-gray-500">Recent payments for this subscription.</p>
                </div>
              </div>

              {payments.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">No invoices found yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="px-4 py-3">ID</th>
                        <th className="px-4 py-3">Period</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Due date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Invoice</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {payments.map((payment) => {
                        const paymentStatus = (payment.status || '').toLowerCase();
                        const isOverduePayment = OVERDUE_STATUSES.has(paymentStatus);
                        const badgeClass = statusStyles[payment.status] || 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
                        const pauseDetail = parsePauseNote(payment.notes);
                        const genericJson = !pauseDetail ? parseGenericJsonNote(payment.notes) : null;
                        return (
                          <tr key={payment.id} className="transition-colors duration-150 hover:bg-[#f7f9ff]">
                            <td className="px-4 py-3">
                              {pauseDetail ? (
                                <div className="space-y-2">
                                  <div className="font-medium text-gray-900">Pause schedule</div>
                                  <button
                                    type="button"
                                   className="text-sm text-blue-600 underline decoration-blue-200 underline-offset-4 transition-colors duration-150 hover:text-blue-800"
                                    onClick={() => {
                                      setNoteModalType('pause');
                                      setNoteDetail(pauseDetail);
                                      setNoteModalOpen(true);
                                    }}
                                  >
                                    View details
                                  </button>
                                </div>
                              ) : genericJson ? (
                                <div className="space-y-2">
                                  <div className="font-medium text-gray-900">Subscription note</div>
                                  <button
                                    type="button"
                                     className="text-sm text-blue-600 underline decoration-blue-200 underline-offset-4 transition-colors duration-150 hover:text-blue-800"
                                    onClick={() => {
                                      setNoteModalType('json');
                                      setNoteDetail(genericJson);
                                      setNoteModalOpen(true);
                                    }}
                                  >
                                    View details
                                  </button>
                                </div>
                              ) : (
                                <div className="font-medium text-gray-900 break-words">
                                  {formatNote(payment.notes) || payment.id}
                                </div>
                              )}
                              {/* <div className="text-xs text-gray-500">Stripe: {payment.stripeInvoiceId || '--'}</div> */}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              <div>{formatDate(payment.periodFrom)}</div>
                              <div className="text-xs text-gray-500">to {formatDate(payment.periodTo)}</div>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {formatCurrency(payment.amountTotal, billing?.subscription?.currency)}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{formatDate(payment.dueDate)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}>
                                {statusLabel(payment.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {payment.invoiceUrl ? (
                                <a
                                  href={payment.invoiceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                   className={`inline-flex items-center gap-1 text-sm font-medium transition-all duration-150 hover:gap-1.5 ${isOverduePayment
                                       ? 'text-red-600 hover:text-red-800'
                                       : 'text-blue-600 hover:text-blue-800'
                                     }`}
                                >
                                  {isOverduePayment ? 'Pay now' : 'View'}
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                </a>
                              ) : isOverduePayment ? (
                                <button
                                  type="button"
                                  onClick={handleOpenPortal}
                                  disabled={portalLoading}
                                   className={`inline-flex items-center gap-1 text-sm font-medium transition-all duration-150 hover:gap-1.5 ${portalLoading
                                       ? 'text-gray-400 cursor-not-allowed'
                                       : 'text-red-600 hover:text-red-800'
                                     }`}
                                >
                                  {portalLoading ? 'Opening...' : 'Pay now'}
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">--</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

              <div className="space-y-4">
               <div className="rounded-[24px] border border-[#e7ecf8] bg-white p-5 shadow-[0_14px_30px_rgba(15,23,42,0.05)]">
                <h3 className="text-lg font-semibold text-gray-900">Subscription details</h3>
                <dl className="mt-3 space-y-3 text-sm text-gray-700">
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-gray-500">Customer email</dt>
                    <dd className="font-medium text-gray-900 text-right">{billing?.email || '--'}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-gray-500">Valid from</dt>
                    <dd className="font-medium text-gray-900">{formatDate(subscription?.validFrom)}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-gray-500">Valid to</dt>
                    <dd className="font-medium text-gray-900">{ billing?.subscription.status !== 'past_due'? 'Continuously Active' : 'Suspended'}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <dt className="text-gray-500">Notes</dt>
                    <dd className="text-right text-gray-900">
                      {subscriptionNotePause ? (
                         <button
                           type="button"
                           className="text-sm text-blue-600 underline decoration-blue-200 underline-offset-4 transition-colors duration-150 hover:text-blue-800"
                          onClick={() => {
                            setNoteModalType('pause');
                            setNoteDetail(subscriptionNotePause);
                            setNoteModalOpen(true);
                          }}
                        >
                          View pause details
                        </button>
                      ) : subscriptionNoteJson ? (
                         <button
                           type="button"
                           className="text-sm text-blue-600 underline decoration-blue-200 underline-offset-4 transition-colors duration-150 hover:text-blue-800"
                          onClick={() => {
                            setNoteModalType('json');
                            setNoteDetail(subscriptionNoteJson);
                            setNoteModalOpen(true);
                          }}
                        >
                          View details
                        </button>
                      ) : (
                        subscription?.notes || '-'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {hasStripeCustomerId ? (
                 <div className="rounded-[24px] border border-[#e7ecf8] bg-white p-5 shadow-[0_14px_30px_rgba(15,23,42,0.05)]">
                  <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
                  <div className="mt-4 space-y-4 text-sm text-gray-700">
                    {isCanceled ? (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={handleResubscribe}
                          disabled={!canResubscribe}
                           className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium shadow transition-all duration-200 hover:-translate-y-0.5 ${canResubscribe
                               ? 'bg-blue-600 text-white hover:bg-blue-700'
                               : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                             }`}
                        >
                          Resubscribe
                        </button>
                        <div className="text-xs text-gray-500">Endpoint: {resubscribeEndpoint}</div>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                       <button
                         type="button"
                         onClick={handleOpenPortal}
                         disabled={portalLoading}
                         className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium shadow transition-all duration-200 hover:-translate-y-0.5 ${portalLoading
                             ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                             : 'bg-blue-600 text-white hover:bg-blue-700'
                           }`}
                      >
                        {portalLoading ? 'Opening portal...' : 'Change payment method'}
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

               <div className="rounded-[20px] bg-blue-50 p-4 text-sm text-blue-900 ring-1 ring-blue-100 shadow-[0_12px_24px_rgba(59,130,246,0.08)]">
                Keep an eye on the next payment date. Refresh if you update billing settings in Stripe.
              </div>
              {!isCanceled && hasStripeCustomerId ? (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setCancelModalOpen(true)}
                    className="text-sm font-medium text-red-600 underline decoration-red-200 underline-offset-4 transition-colors duration-150 hover:text-red-700"
                  >
                    Cancel My Subscription
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      <Modal
        open={noteModalOpen}
        onCancel={() => setNoteModalOpen(false)}
        footer={null}
        title={noteModalType === 'pause' ? 'Pause history' : 'Notes'}
      >
        {noteModalType === 'pause' ? (
          Array.isArray(noteDetail) && noteDetail.length ? (
            <div className="space-y-4">
              {noteDetail.map((item, idx) => (
                <div key={idx} className="rounded-md border border-gray-200 p-3">
                  <div className="text-sm font-semibold text-gray-800">
                    {formatDateTime(item.startAt)} {' -> '} {formatDateTime(item.endAt)}
                  </div>
                  {Array.isArray(item.extensions) && item.extensions.length ? (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium text-gray-600">Extensions</div>
                      {item.extensions.map((ext, i) => (
                        <div key={i} className="text-xs text-gray-700">
                          {formatDateTime(ext.startAt)} {' -> '} {formatDateTime(ext.endAt)}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-600">No pause details found.</div>
          )
        ) : (
          <pre className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs text-gray-800 border border-gray-200">
            {JSON.stringify(noteDetail, null, 2)}
          </pre>
        )}
      </Modal>

      <Modal
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        footer={null}
        title="Cancel subscription"
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Your subscription cancellation will be managed securely in the billing portal.
          </p>
          <p>
            Access will typically remain active until the end of the current billing period, based on your Stripe portal settings.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCancelModalOpen(false)}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50"
            >
              Keep Subscription
            </button>
            <button
              type="button"
              onClick={handleStartCancellation}
              disabled={portalLoading}
              className={`rounded-xl px-4 py-2 text-sm font-medium text-white transition-all duration-150 ${
                portalLoading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {portalLoading ? 'Opening portal...' : 'Continue to Cancellation'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Billing;








